// Technical strings inside server-built Arabic sentences (document numbers
// «#ق-0152», account codes «5600», ids) render LTR Latin in an isolate
// (BRIEF hard rule 1). erp-core sends sentences such as
// «فاتورة #0032 بتاريخ ٢٨ آب …» or «سند صرف #ص-0011 · ٩٠٠٬٠٠٠ د.ع»; this splits
// them so the number sits in an LTR isolate and the Arabic flows around it.
//
// A document number with an Arabic series letter («#ص-0011») is an RTL run
// even inside `direction:ltr; unicode-bidi:isolate` — the letter is AL, so the
// browser draws «ص-0011#». `.bc-docno` adds `unicode-bidi:isolate-override`
// (see close.css) so it renders exactly as typed, as the boards draw it
// (SHARED-REQUESTS: a shared `.ltr-doc` class would replace this).

import type { ReactNode } from 'react';

/** `#<series>-<n>` / `#<n>` document numbers and bare 4-digit Latin account codes. */
const TECH = /(#[^\s،,·)(]+|\b[0-9]{4}\b)/g;

/** One technical token, isolated LTR (override for document numbers). */
export function Tech({ children }: { children: string }) {
  return (
    <span className="ltr bc-docno" dir="ltr">
      {children}
    </span>
  );
}

export function isolateCodes(text: string | null | undefined): ReactNode {
  if (!text) return text ?? null;
  const parts = text.split(TECH);
  if (parts.length === 1) return text;
  return parts.map((p, i) => (i % 2 === 1 ? <Tech key={i}>{p}</Tech> : p));
}

/**
 * Plain-text isolate for strings that cannot carry markup (toasts, aria):
 * LRI … PDI around every technical token.
 */
export function isolateText(text: string): string {
  return text.replace(TECH, (m) => `⁦${m}⁩`);
}
